<template>
  <div class="ocr-excel-panel">
    <OcrUploadPanel
      v-model="pickedFiles"
      :uploading="uploading"
      :upload-error="uploadError"
      excel-mode
      @upload="uploadExcel"
    />


    <OcrProcessStatus v-if="jobStatus" :job="jobStatus" />

    <OcrExcelFileGrid
      v-model:selected="selected"
      :refresh-key="refreshKey"
      bucket="uploaded"
      :processing-file="processingOneFile || jobStatus?.current_file"
      @process-one="processOne"
      @error="gridError = $event"
    />

    <div class="ocr-upload-workspace-actions ocr-panel ocr-excel-process-panel">
      <p class="ocr-panel-lead ocr-panel-lead--flush">
        Split multi-table sheets, export to PDF, extract tables, map and save report.
      </p>
      <v-switch
        v-model="splitMultiTable"
        color="primary"
        density="compact"
        hide-details
        label="Process sheet with multiple tables"
      />
      <v-switch
        v-if="splitMultiTable"
        v-model="formatSplitSheets"
        color="primary"
        density="compact"
        hide-details
        label="Format split sheets to match original"
      />
      <v-switch
        v-if="splitMultiTable"
        v-model="splitFirstSheetOnly"
        color="primary"
        density="compact"
        hide-details
        label="Only split first sheet"
      />
      <div class="ocr-panel-actions ocr-panel-actions--stack">
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!selected.length || processing"
          :loading="processing"
          @click="processSelected"
        >
          Process selected ({{ selected.length }})
        </v-btn>
      </div>
    </div>

    <div class="ocr-upload-lists">
      <OcrFileList
        title="Processing queue"
        bucket="processing"
        extensions=".xlsx,.xlsm"
        :names="queuedFiles"
        :active-name="jobStatus?.current_file || ''"
        :refresh-key="refreshKey"
      />
      <div class="ocr-upload-completed">
        <OcrFileList
          title="Completed Excel files"
          bucket="completed"
          extensions=".xlsx,.xlsm"
          selectable
          :selected="selectedCompleted"
          :refresh-key="refreshKey"
          @update:selected="selectedCompleted = $event"
        />
        <div class="ocr-upload-workspace-actions ocr-panel">
          <p class="ocr-panel-lead ocr-panel-lead--flush">
            Move completed Excel files back to uploaded so they can be processed again.
          </p>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!selectedCompleted.length || requeueing"
            :loading="requeueing"
            @click="requeueCompleted"
          >
            Reprocess selected ({{ selectedCompleted.length }})
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrExcelFileGrid from "@/components/layout/OCR/OcrExcelFileGrid.vue";
import OcrFileList from "@/components/layout/OCR/OcrFileList.vue";
import OcrProcessStatus from "@/components/layout/OCR/OcrProcessStatus.vue";
import OcrUploadPanel from "@/components/layout/OCR/OcrUploadPanel.vue";
import { ocrNotifyMixin } from "@/components/layout/OCR/OcrSnackbar.vue";

const TERMINAL = new Set(["completed", "completed_with_errors", "failed"]);

export default {
  name: "OcrExcelPanel",
  mixins: [ocrNotifyMixin],
  components: { OcrExcelFileGrid, OcrFileList, OcrProcessStatus, OcrUploadPanel },
  props: {
    listKey: { type: Number, default: 0 },
  },
  emits: ["refresh"],
  data() {
    return {
      pickedFiles: [],
      uploading: false,
      uploadError: "",
      processing: false,
      processingOneFile: null,
      selected: [],
      selectedCompleted: [],
      requeueing: false,
      splitMultiTable: true,
      formatSplitSheets: false,
      splitFirstSheetOnly: true,
      statusMessage: "",
      statusType: "info",
      gridError: "",
      localKey: 0,
      jobStatus: null,
      pollToken: 0,
    };
  },
  computed: {
    refreshKey() {
      return this.listKey + this.localKey;
    },
    queuedFiles() {
      if (this.jobStatus?.queued_files) return this.jobStatus.queued_files;
      const files = this.jobStatus?.files || [];
      const done = new Set([
        ...(this.jobStatus?.success_files || []),
        ...(this.jobStatus?.failed_files || []),
      ]);
      return files.filter((name) => !done.has(name));
    },
  },
  watch: {
    uploadError(message) {
      if (message) this.ocrToast(message, "error");
    },
    gridError(message) {
      if (message) this.ocrToast(message, "error");
    },
    statusMessage(message) {
      if (!message || this.jobStatus) return;
      this.$nextTick(() => {
        if (this.statusMessage && !this.jobStatus) this.ocrToast(this.statusMessage, this.statusType);
      });
    },
  },
  mounted() {
    this.resumeActiveJob();
  },
  beforeUnmount() {
    this.pollToken += 1;
  },
  methods: {
    bumpLists() {
      this.localKey += 1;
      this.$emit("refresh");
    },
    async resumeActiveJob() {
      try {
        const { data } = await ocrApi.get("api/v1/files/process/active");
        if (!data?.running || data.kind !== "excel") return;
        this.jobStatus = data;
        this.processing = true;
        if (data.job_id) await this.pollJob(data.job_id);
      } catch {
        /* no active excel job */
      } finally {
        this.processing = false;
        this.bumpLists();
      }
    },
    async uploadExcel() {
      if (!this.pickedFiles?.length) return;
      this.uploading = true;
      this.uploadError = "";
      try {
        const form = new FormData();
        for (const f of this.pickedFiles) {
          form.append("files", f);
        }
        const { data } = await ocrApi.post("api/v1/files/upload?kind=excel", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.errors?.length) {
          this.uploadError = data.errors.join(" ");
        }
        this.pickedFiles = [];
        this.bumpLists();
      } catch (e) {
        this.uploadError = formatOcrApiError(e, "Excel upload failed.");
      } finally {
        this.uploading = false;
      }
    },
    async runExcelJob(files) {
      const { data } = await ocrApi.post("api/v1/files/process/excel/start", {
        files,
        split_multi_table: this.splitMultiTable,
        format_split_sheets: this.formatSplitSheets,
        split_first_sheet_only: this.splitFirstSheetOnly,
      });
      this.jobStatus = data;
      if (data.job_id) {
        await this.pollJob(data.job_id);
      }
    },
    async processSelected() {
      if (!this.selected.length) return;
      this.processing = true;
      this.statusMessage = "";
      try {
        await this.runExcelJob(this.selected);
        this.selected = [];
        this.bumpLists();
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Excel processing failed.");
        this.statusType = "error";
        this.jobStatus = null;
      } finally {
        this.processing = false;
      }
    },
    async processOne(filename) {
      this.processingOneFile = filename;
      this.statusMessage = "";
      try {
        await this.runExcelJob([filename]);
        this.selected = this.selected.filter((f) => f !== filename);
        this.bumpLists();
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Excel processing failed.");
        this.statusType = "error";
        this.jobStatus = null;
      } finally {
        this.processingOneFile = null;
      }
    },
    async requeueCompleted() {
      if (!this.selectedCompleted.length) return;
      this.requeueing = true;
      try {
        await ocrApi.post("api/v1/files/requeue", {
          files: this.selectedCompleted,
          from_bucket: "completed",
        });
        this.selectedCompleted = [];
        this.statusMessage = "Moved to uploaded. Select them and click Process selected.";
        this.statusType = "success";
        this.jobStatus = null;
        this.bumpLists();
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Re-queue failed.");
        this.statusType = "error";
      } finally {
        this.requeueing = false;
      }
    },
    async pollJob(jobId) {
      const token = (this.pollToken += 1);
      let ticks = 0;
      while (token === this.pollToken) {
        await new Promise((r) => setTimeout(r, 2000));
        if (token !== this.pollToken) return;
        try {
          const { data } = await ocrApi.get(`api/v1/files/process/status/${jobId}`);
          this.jobStatus = data;
          this.statusMessage = data.message || data.status;
          if (data.status === "failed") this.statusType = "error";
          else if (data.status === "completed_with_errors") this.statusType = "warning";
          else if (data.status === "completed") this.statusType = "success";
          else this.statusType = "info";
          ticks += 1;
          if (ticks % 2 === 0) this.bumpLists();
          if (TERMINAL.has(data.status)) break;
        } catch (e) {
          if (e?.response?.status === 404) break;
        }
      }
      this.bumpLists();
    },
  },
};
</script>
